/**
 * Traducción de los nombres crudos que devuelve `GET /me` a lenguaje humano.
 *
 * Quien lee esta pantalla es un técnico de ascensores, un ejecutivo o un
 * administrador de edificio — no un ingeniero de software. El nombre de la tabla
 * (`attention_voucher`, `actividad_hallazgo`) y el del rol (`tecnico_responsable`)
 * NUNCA se muestran tal cual: son detalles internos y no significan nada para la
 * persona que ejerce sus derechos.
 *
 * La misma tabla aparece con roles distintos y significan cosas distintas: una
 * mantención donde fuiste el técnico no es lo mismo que una donde firmaste como
 * quien recibió el trabajo. Por eso la clave principal es `tabla|rol` y no la
 * tabla sola.
 */

/** Etiqueta preferida, por combinación exacta de tabla y rol. */
const ETIQUETAS_POR_TABLA_Y_ROL = {
  'mantencion|tecnico_responsable': {
    titulo: 'Mantenciones que realizaste',
    detalle: 'Trabajos de mantención en los que quedaste registrado como el técnico a cargo.',
  },
  'mantencion|recepcionista': {
    titulo: 'Mantenciones que recibiste',
    detalle: 'Mantenciones donde firmaste como la persona que recibió el trabajo terminado.',
  },
  'attention_voucher|tecnico_responsable': {
    titulo: 'Comprobantes de atención que emitiste',
    detalle: 'Comprobantes de visitas de atención en los que figuras como el técnico.',
  },
  'attention_voucher|recepcionista': {
    titulo: 'Comprobantes de atención que recibiste',
    detalle: 'Comprobantes donde firmaste como la persona que recibió la atención.',
  },
  'order|tecnico_responsable': {
    titulo: 'Órdenes de trabajo que atendiste',
    detalle: 'Órdenes de trabajo asignadas a ti como técnico.',
  },
  'order|recepcionista': {
    titulo: 'Órdenes de trabajo que recibiste',
    detalle: 'Órdenes donde firmaste como la persona que recibió el trabajo.',
  },
  'solicitud_ejecutivo|tecnico_responsable': {
    titulo: 'Solicitudes de servicio asignadas a ti',
    detalle: 'Pedidos de servicio que se te asignaron para atender.',
  },
  'actividad_hallazgo|tecnico_responsable': {
    titulo: 'Observaciones que registraste',
    detalle: 'Problemas o detalles que anotaste al revisar un equipo.',
  },
  'reporte_visita|tecnico_responsable': {
    titulo: 'Reportes de visita que escribiste',
    detalle: 'Informes de visitas a clientes redactados por ti.',
  },
  'reporte_visita|cliente': {
    titulo: 'Reportes de visita donde apareces como cliente',
    detalle: 'Informes de visita en los que se te menciona como la persona visitada.',
  },
  'client|cliente': {
    titulo: 'Tu ficha de cliente',
    detalle: 'Los datos de contacto que guardamos de ti como cliente.',
  },
  'incident_reports|reportero': {
    titulo: 'Problemas que reportaste',
    detalle: 'Avisos de fallas o sugerencias que enviaste al equipo de soporte.',
  },
  'administrador|titular_cuenta': {
    titulo: 'Tu ficha de administrador de edificio',
    detalle: 'Los datos que guardamos de ti como administrador.',
  },
  'especialistas|titular_cuenta': {
    titulo: 'Tu ficha de técnico',
    detalle: 'Los datos que guardamos de ti como técnico.',
  },
  'user|titular_cuenta': {
    titulo: 'Tu cuenta de acceso',
    detalle: 'El nombre, correo y RUT con los que entras a la plataforma.',
  },
};

/**
 * Respaldo por tabla, si el backend empieza a emitir un rol nuevo para una tabla
 * que ya conocemos. Preferimos una frase menos precisa antes que mostrar
 * `attention_voucher` en pantalla.
 */
const ETIQUETAS_POR_TABLA = {
  mantencion: { titulo: 'Mantenciones en las que participaste' },
  attention_voucher: { titulo: 'Comprobantes de atención en los que apareces' },
  order: { titulo: 'Órdenes de trabajo en las que apareces' },
  solicitud_ejecutivo: { titulo: 'Solicitudes de servicio en las que apareces' },
  actividad_hallazgo: { titulo: 'Observaciones registradas en revisiones' },
  reporte_visita: { titulo: 'Reportes de visita en los que apareces' },
  client: { titulo: 'Tu ficha de cliente' },
  incident_reports: { titulo: 'Problemas que reportaste' },
  administrador: { titulo: 'Tu ficha de administrador de edificio' },
  especialistas: { titulo: 'Tu ficha de técnico' },
  user: { titulo: 'Tu cuenta de acceso' },
};

/** Último recurso: qué decir cuando ni la tabla ni el rol nos suenan. */
const ETIQUETA_GENERICA = {
  titulo: 'Otros registros',
  detalle: 'Registros del sistema en los que apareces.',
};

/** En qué calidad aparece la persona en ese grupo de registros. */
const ETIQUETAS_ROL = {
  titular_cuenta: 'Tu cuenta',
  tecnico_responsable: 'Como técnico',
  recepcionista: 'Como quien recibió el trabajo',
  cliente: 'Como cliente',
  reportero: 'Como quien reportó',
};

/**
 * Traduce una fila del `resumen` a algo legible.
 *
 * Nunca devuelve el string crudo: si la tabla o el rol son desconocidos —porque
 * el backend agregó una fuente nueva y esta pantalla todavía no se enteró— cae en
 * una etiqueta genérica digna. Es preferible un "Otros registros" a filtrar el
 * nombre de una tabla a la cara del usuario.
 *
 * @param {string} tabla nombre interno de la tabla, ej. `attention_voucher`
 * @param {string} rol calidad en que aparece, ej. `tecnico_responsable`
 * @returns {{ titulo: string, detalle?: string, rol: string|null, conocido: boolean }}
 */
export const traducirAparicion = (tabla, rol) => {
  const etiquetaExacta = ETIQUETAS_POR_TABLA_Y_ROL[`${tabla}|${rol}`];
  const etiquetaTabla = ETIQUETAS_POR_TABLA[tabla];
  const etiqueta = etiquetaExacta ?? etiquetaTabla ?? ETIQUETA_GENERICA;

  return {
    titulo: etiqueta.titulo,
    detalle: etiqueta.detalle,
    rol: ETIQUETAS_ROL[rol] ?? null,
    conocido: Boolean(etiquetaExacta ?? etiquetaTabla),
  };
};

/** Etiqueta humana de un rol, o null si no lo conocemos (nunca el string crudo). */
export const traducirRol = (rol) => ETIQUETAS_ROL[rol] ?? null;

/**
 * Los tres derechos que necesitan que alguien los revise antes de ejecutarse.
 * Los otros tres (ver, corregir y descargar) se ejercen en la misma pantalla.
 *
 * El orden importa: primero el que más se pide.
 */
export const TIPOS_SOLICITUD_ETIQUETAS = {
  supresion: {
    titulo: 'Pedir que borremos tus datos',
    detalle:
      'Revisaremos qué podemos borrar. Algunos registros de los trabajos realizados debemos conservarlos por obligación legal.',
    irreversible: true,
  },
  oposicion: {
    titulo: 'Pedir que dejemos de usar tus datos para algo',
    detalle: 'Cuéntanos para qué uso específico no quieres que usemos tus datos.',
    irreversible: false,
  },
  bloqueo: {
    titulo: 'Pedir que congelemos tus datos por un tiempo',
    detalle:
      'Tus datos se guardan pero dejan de usarse mientras se revisa tu caso. No se borra nada.',
    irreversible: false,
  },
};

export default traducirAparicion;
